import { FC, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Package, Link2, QrCode } from 'lucide-react'
// Using DaisyUI classes directly instead of custom components
import { Typography } from '@/components/ui/typography'
import { Motion } from '@/components/ui/motion'
import { Button } from '@/components/ui/button'
import Header from '@/components/Header'
import ErrorView from '@/components/ErrorView'
import LoadingView from '@/components/LoadingView'
import BottomNavigation from '@/components/BottomNavigation'
import { useProjectData, useProjectFromAsset } from '@/hooks/useProjectData'
import { useAuthStore } from '@/store/auth.store'
import { useTranslation } from '@/hooks/useTranslation'
import { formatFrameFinish, formatFrameModel, formatWindowRoomTitle } from '@/utils/windowPosition'

interface EquipmentCard {
  /** Row key: the order row id, or the QR serial for tags without a row. */
  uuid: string
  /** Serial of the QR tag that manages this row, when the room has one. */
  qrTagSerial?: string
  type: 'placement' | 'linkedStructure'
  hasPositionAssociation?: boolean
  positionNumber?: string
  positionDescription?: string
  buildingFloor?: string
  articleDescription?: string
  dimensions?: string
  brand?: string
  model?: string
  modelDescription?: string
}

type StructureInfo = Record<string, unknown> | undefined

const readInfo = (info: StructureInfo, key: string): string =>
  (info?.[key] as string) || ''

/**
 * One card per order row. A window room holds more than one article — the frame
 * and its shutter share the same opening — and the installer has to see every
 * one of them, so the list is built from the project positions rather than from
 * the QR tags (there is a single tag per room).
 */
const toCard = (
  uuid: string,
  info: StructureInfo,
  type: EquipmentCard['type']
): EquipmentCard => {
  const positionNumber = readInfo(info, 'positionNumber')
  const positionDescription = readInfo(info, 'description')

  return {
    uuid,
    qrTagSerial: readInfo(info, 'qrTagSerial') || undefined,
    type,
    // Rows without any position data are all serial and nothing else: that is
    // everything we know about them.
    hasPositionAssociation: positionNumber.trim() !== '' || positionDescription.trim() !== '',
    positionNumber,
    positionDescription,
    buildingFloor: readInfo(info, 'floorNumber'),
    articleDescription: readInfo(info, 'articleDescription'),
    dimensions: readInfo(info, 'dimensions'),
    brand: readInfo(info, 'brand'),
    model: readInfo(info, 'model'),
    modelDescription: readInfo(info, 'modelDescription')
  }
}

const ProjectDashboard: FC = () => {
  const navigate = useNavigate()
  const { projectId, uuid } = useParams<{ projectId?: string; uuid?: string }>()
  const [equipmentCards, setEquipmentCards] = useState<EquipmentCard[]>([])
  const [hasProjectPermission, setHasProjectPermission] = useState<boolean>(false)
  const { t } = useTranslation()
  const { isAuthenticated, isInstaller } = useAuthStore()

  // Determine which hook to use based on route parameters
  const isFromEquipment = !!uuid && !projectId

  // Use appropriate hook based on route type
  const projectDataResult = useProjectData(isFromEquipment ? undefined : projectId)
  const projectFromAssetResult = useProjectFromAsset(isFromEquipment ? uuid : undefined)

  // Extract data from the appropriate result
  const { project, isLoading, error } = isFromEquipment ? projectFromAssetResult : projectDataResult

  // Process equipment cards when project data changes
  useEffect(() => {
    if (!project) {
      setEquipmentCards([])
      return
    }

    const placements = (project.placements || []) as Array<{
      uuid: string
      info?: Record<string, unknown>
    }>
    const linkedStructures = (project.linkedStructures || []) as Array<{
      uuid: string
      info?: Record<string, unknown>
    }>

    const cards: EquipmentCard[] = placements.map(placement =>
      toCard(placement.uuid, placement.info, 'placement')
    )

    // Tags linked to no order row would otherwise vanish from the project: keep
    // them listed on their own so they stay reachable.
    const coveredSerials = new Set(cards.map(card => card.qrTagSerial).filter(Boolean))

    linkedStructures.forEach(structure => {
      if (coveredSerials.has(structure.uuid)) return
      cards.push(toCard(structure.uuid, structure.info, 'linkedStructure'))
    })

    setEquipmentCards(cards)
  }, [project])

  // Check installer permissions for regular project routes
  useEffect(() => {
    const checkProjectPermissions = async () => {
      const key = project?.uuid || projectId

      if (key && projectDataResult.service) {
        try {
          const hasPermission = await projectDataResult.service.checkInstallerProjectPermission(key)
          setHasProjectPermission(hasPermission)
        } catch (permError) {
          console.warn('Failed to check installer permissions:', permError)
          setHasProjectPermission(false)
        }
      }
    }

    checkProjectPermissions()
  }, [isFromEquipment, project, projectDataResult.service])

  const handleEquipmentClick = (equipmentUuid: string) => {
    navigate(`/view/${equipmentUuid}`)
  }

  const handleManageLinkings = () => {
    // Navigate to linking page with the project UUID
    // When coming from from-equipment route, prioritize project.uuid over the equipment uuid
    const targetUuid = project?.uuid || projectId
    if (targetUuid) {
      navigate(`/linking/${targetUuid}`)
    }
  }

  if (isLoading) {
    return <LoadingView message={t('LOADING_PROJECT_DASHBOARD')} />
  }

  if (error) {
    return <ErrorView message={error} />
  }

  if (!project) {
    return (
      <div className='flex h-screen items-center justify-center bg-[hsl(var(--background))]'>
        <Typography variant='h4'>No project data found</Typography>
      </div>
    )
  }

  const projectInfo = project.info
  const projectTitle = projectInfo.identification || 'Untitled Project'

  return (
    <div className='min-h-screen bg-[hsl(var(--background))] pb-24'>
      <Header
        title={t('PROJECT_DASHBOARD_TITLE') + ' - ' + projectTitle}
        backTo={isAuthenticated && isInstaller ? '/my-projects' : '/go'}
      />
      <div className='container mx-auto px-4 py-6'>
        <Motion variant='fadeIn' duration={500}>
          {/* Manage Linkings Card - Only visible to installers with project permissions */}
          {isInstaller && hasProjectPermission && (
            <div className='mb-6'>
              <Motion variant='slideUp' duration={300}>
                <div
                  className='rounded-xl bg-[hsl(var(--primary))] text-white cursor-pointer card-hover shadow-md'
                  onClick={handleManageLinkings}
                >
                  <div className='p-5'>
                    <div className='flex items-center gap-4'>
                      <Link2 className='h-8 w-8' />
                      <div>
                        <h3 className='text-lg font-semibold'>{t('MANAGE_LINKINGS_TITLE')}</h3>
                        <p className='text-sm text-white/80'>{t('MANAGE_LINKINGS_DESCRIPTION')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Motion>
            </div>
          )}

          <div className='mb-6'>
            <h2 className='text-2xl font-semibold mb-4'>
              {t('WINDOW_FRAME_ELEMENTS_TITLE')} ({equipmentCards.length})
            </h2>

            {equipmentCards.length === 0 ? (
              <div className='rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]'>
                <div className='flex items-center justify-center p-12'>
                  <div className='text-center'>
                    <Package className='mx-auto h-12 w-12 mb-4' />
                    <h4 className='text-xl'>{t('NO_ELEMENTS_ASSIGNED')}</h4>
                    <p className='text-sm mt-2'>{t('NO_ELEMENTS_ASSIGNED_DESCRIPTION')}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
                {equipmentCards.map((equipment, index) => {
                  const frameModel = formatFrameModel(equipment)
                  const frameFinish = formatFrameFinish(equipment)

                  return (
                    <Motion key={equipment.uuid} variant='slideUp' duration={300} delay={index * 100}>
                      {/* The card itself is not clickable: only "Gestisci" reacts
                          to the mouse, so the whole row does not look actionable. */}
                      <div className='rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]'>
                        <div className='p-5'>
                          <div className='flex items-center gap-2 mb-3'>
                            {equipment.hasPositionAssociation ? (
                              <Package className='h-5 w-5 text-[var(--brand-red)]' />
                            ) : (
                              <QrCode className='h-5 w-5 text-[var(--brand-red)]' />
                            )}
                            <span className='text-sm'>
                              {equipment.hasPositionAssociation
                                ? formatWindowRoomTitle(t('LINKING_POSITION_NUMBER'), {
                                    positionNumber: equipment.positionNumber,
                                    floor: equipment.buildingFloor,
                                    description: equipment.positionDescription
                                  })
                                : t('LINKING_LINKED_QR_TAG')}
                            </span>
                          </div>

                          {/* QR serial of the window room - Main Title */}
                          <h4 className='text-lg font-semibold mb-2'>
                            {equipment.qrTagSerial || t('LINKING_NO_LINKED_QR_TAG')}
                          </h4>

                          {/* Model code + model description */}
                          {frameModel && <p className='text-sm font-medium mb-2'>{frameModel}</p>}

                          {/* Finish description + measures */}
                          {frameFinish && (
                            <p className='text-sm text-[hsl(var(--muted-foreground))] mb-2'>{frameFinish}</p>
                          )}

                          {equipment.qrTagSerial && (
                            <Button
                              size='sm'
                              className='mt-2 w-full'
                              onClick={() => handleEquipmentClick(equipment.qrTagSerial!)}
                            >
                              {t('LINKING_MANAGE')}
                            </Button>
                          )}
                        </div>
                      </div>
                    </Motion>
                  )
                })}
              </div>
            )}
          </div>
        </Motion>
      </div>
      <BottomNavigation projectId={project?.uuid} equipmentUuid={uuid} />
    </div>
  )
}

export default ProjectDashboard
